import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, loginRequest } from '../../hooks/msalConfig';
import GetFetchGraph from '../../helper/GetFetchGraph';

// Widget compacto de correo institucional para el Dashboard de alumnos.
// Reutiliza la sesión MSAL del usuario logueado en APPESEN: SOLO muestra
// el resumen si hay una cuenta Microsoft cacheada que coincide con su correo
// y se puede obtener token en silencio (NO abre popup desde el dashboard).
// La experiencia completa vive en /correo.
const DashCorreo = ({ correoEsperado }) => {
    const [estado, setEstado] = useState('cargando'); // cargando | sin-sesion | ok | error
    const [mensajes, setMensajes] = useState([]);
    const [noLeidos, setNoLeidos] = useState(0);

    const esperado = (correoEsperado || '').trim().toLowerCase();

    const cuentaDelUsuario = useCallback(() => {
        if (!esperado) return null;
        return msalInstance.getAllAccounts().find(
            (c) => (c.username || '').toLowerCase() === esperado
        ) || null;
    }, [esperado]);

    useEffect(() => {
        let activo = true;
        const acc = cuentaDelUsuario();
        if (!acc) {
            setEstado('sin-sesion');
            return;
        }
        msalInstance
            .acquireTokenSilent({ ...loginRequest, account: acc })
            .then((resp) =>
                Promise.all([
                    GetFetchGraph.getMensajes(resp.accessToken, 3),
                    GetFetchGraph.getInboxUnread(resp.accessToken),
                ])
            )
            .then(([data, unread]) => {
                if (!activo) return;
                setMensajes(data.value || []);
                setNoLeidos(unread);
                setEstado('ok');
            })
            .catch((e) => {
                if (!activo) return;
                // Sin token en silencio: pedir interacción es tarea de /correo,
                // no del dashboard. Lo tratamos como "sin sesión".
                setEstado(e instanceof InteractionRequiredAuthError ? 'sin-sesion' : 'error');
            });
        return () => {
            activo = false;
        };
    }, [cuentaDelUsuario]);

    const formatoFecha = (iso) => {
        try {
            return new Date(iso).toLocaleString('es-SV', {
                day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
            });
        } catch (e) {
            return iso;
        }
    };

    return (
        <div className="card">
            <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h3 className="h6 mb-0">
                        <i className="fa fa-envelope-o mr-2" aria-hidden="true"></i>
                        Correo institucional
                        {estado === 'ok' && noLeidos > 0 ? (
                            <span className="badge badge-primary ml-2">{noLeidos} sin leer</span>
                        ) : null}
                    </h3>
                    <Link to="/correo" className="small">Ver todos</Link>
                </div>

                {estado === 'cargando' ? (
                    <div className="text-muted small py-2">
                        <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                        Cargando…
                    </div>
                ) : estado === 'sin-sesion' ? (
                    <p className="text-muted small mb-0">
                        Conecta tu correo institucional de Microsoft para verlo aquí.{' '}
                        <Link to="/correo">Ir a correo</Link>
                    </p>
                ) : estado === 'error' ? (
                    <p className="text-muted small mb-0">
                        No se pudo cargar el correo por ahora.{' '}
                        <Link to="/correo">Abrir correo</Link>
                    </p>
                ) : mensajes.length === 0 ? (
                    <p className="text-muted small mb-0">No tienes correos recientes.</p>
                ) : (
                    <div className="list-group list-group-flush">
                        {mensajes.map((m) => (
                            <Link
                                key={m.id || m.webLink}
                                to="/correo"
                                className={`list-group-item list-group-item-action px-0 py-2 ${m.isRead ? '' : 'font-weight-bold'}`}
                            >
                                <div className="d-flex justify-content-between">
                                    <span className="text-truncate mr-2">
                                        {!m.isRead ? (
                                            <span
                                                className="d-inline-block mr-2"
                                                title="No leído"
                                                style={{ width: 8, height: 8, borderRadius: '50%', background: '#0d6efd' }}
                                            />
                                        ) : null}
                                        {m.from && m.from.emailAddress ? m.from.emailAddress.name : 'Desconocido'}
                                    </span>
                                    <small className="text-muted text-nowrap">{formatoFecha(m.receivedDateTime)}</small>
                                </div>
                                <div className="text-truncate small">{m.subject || '(sin asunto)'}</div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DashCorreo;
